import {
  type PropsWithChildren,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import styled, { css } from 'styled-components';
import { Slider as SliderLayout } from '@elfsight/lib-toolkit-slider';
import { changeColorAlpha } from '@elfsight/lib-utils';
import { getBrightInvertedColor } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import classNames from 'classnames';
import { meta } from '../../../meta';
import { useLogos } from '../../logo';
import { useGutterResult, usePaddingResult, useSizeResult } from '../../utils';

const SLIDER_WIDTH_OFFSET = 64;

type SliderProps = PropsWithChildren<{ width: number }>;

export function Slider({ children, width }: SliderProps) {
  const logos = useLogos();
  const {
    logoSize,
    gutter,
    sliderArrowsEnabled,
    sliderSwipeEnabled,
    sliderTransitionDuration,
    sliderPaginationType,
    sliderAutoSlideEnabled,
    sliderAutoSlideDelay,
    sliderAutoSlidePauseOnHover,
    sliderElementsColor,
    logoSizeTablet,
    gutterTablet,
    logoSizeMobile,
    gutterMobile,
    logoPadding,
    logoPaddingTablet,
    logoPaddingMobile,
    displayDeactivation
  } = useSettings();

  const sliderRef = useRef(null);
  const [columnsCount, setColumnsCount] = useState(1);
  const [isDrag, setIsDrag] = useState(false);
  const maxLogoSize = logos.reduce(
    (max, { size }) => (max > size ? max : size),
    0
  );

  const sizeResult = useSizeResult(width, true, maxLogoSize);
  const gutterResult = useGutterResult(width);
  const paddingResult = usePaddingResult(width);

  const columnWidth = sizeResult + gutterResult + 2 * paddingResult;
  const isAutoSlideEnabled = sliderAutoSlideEnabled && !displayDeactivation;

  useLayoutEffect(() => {
    const nextColumnsCount =
      Math.floor((width - SLIDER_WIDTH_OFFSET) / columnWidth) || 1;

    if (nextColumnsCount > children.length) {
      return setColumnsCount(children.length);
    }

    if (nextColumnsCount !== columnsCount) {
      return setColumnsCount(nextColumnsCount);
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [children, width, columnWidth]);

  useLayoutEffect(() => {
    requestAnimationFrame(() => {
      sliderRef.current?.translate(true);
    });
  }, [
    width,
    logoSize,
    gutter,
    columnWidth,
    logoSizeMobile,
    logoSizeTablet,
    gutterMobile,
    gutterTablet,
    paddingResult,
    logoPadding,
    logoPaddingTablet,
    logoPaddingMobile
  ]);

  const outerWidth = columnWidth * columnsCount;
  const progressLineActiveBg =
    isAutoSlideEnabled &&
    changeColorAlpha(
      getBrightInvertedColor(sliderElementsColor, [
        'rgb(255, 255, 255)',
        'rgb(29, 33, 41)'
      ]),
      -0.5
    );

  return (
    <Container
      _sliderArrowsEnabled={sliderArrowsEnabled}
      _sliderPaginationType={sliderPaginationType}
      _displayDeactivation={displayDeactivation}
      _progressLineActiveBg={progressLineActiveBg}
    >
      <Inner _outerWidth={outerWidth}>
        <SliderLayout
          ref={sliderRef}
          appClassnamePrefix={meta.prefix}
          navigationClassName={`${meta.prefix}-slider-navigation`}
          paginationClassName={`${meta.prefix}-slider-pagination`}
          slidesPerView={columnsCount}
          paginationType={sliderPaginationType}
          colors={{
            arrows: {
              color: sliderElementsColor,
              background: 'transparent',
              hover: {
                color: sliderElementsColor,
                background: 'transparent'
              }
            },
            pagination: {
              color: changeColorAlpha(sliderElementsColor, -0.5),
              active: sliderElementsColor
            }
          }}
          loop
          shouldGoToFirstSlideWhenSlidesChange
          paginationAlign="center"
          paginationPosition="outside"
          arrows={sliderArrowsEnabled}
          drag={sliderSwipeEnabled}
          speed={sliderTransitionDuration}
          autoplayDelay={isAutoSlideEnabled ? sliderAutoSlideDelay : 0}
          autoplayPauseOnHover={sliderAutoSlidePauseOnHover}
          onTranslateEnd={() => setIsDrag(false)}
          onTranslateStart={() => setIsDrag(true)}
        >
          {children.map((item) => ({
            ...item,
            props: {
              ...item.props,
              isDrag
            }
          }))}
        </SliderLayout>
      </Inner>
    </Container>
  );
}

const Container = styled.div.attrs({
  className: `${meta.prefix}-slider-container`
})`
  display: block;
  flex-grow: 1;
  max-width: 100%;
  position: relative;
  direction: ltr;

  ${({
    _sliderArrowsEnabled,
    _sliderPaginationType,
    _displayDeactivation,
    _progressLineActiveBg
  }) => css`
    ${_sliderArrowsEnabled && 'padding: 0 32px;'}

    & .${meta.prefix}-slider-slider {
      position: initial;

      ${_sliderPaginationType === 'bullets' && 'padding-bottom: 48px;'}
    }

    & .${meta.prefix}-slider-inner {
      position: initial;
    }

    & .${meta.prefix}-navigation-arrow {
      background-color: transparent;

      ${_sliderPaginationType === 'none' && 'top: 50%;'}
      ${_sliderPaginationType === 'none' && 'transform: translateY(-50%);'}
      ${_sliderPaginationType === 'bullets' && 'top: calc(50% - 48px);'}
      ${_displayDeactivation && 'pointer-events: none;'}
    }

    &
      .${meta.prefix}-slider-pagination,
      &
      .${meta.prefix}-pagination-component
      > div {
      display: inherit;

      ${_sliderPaginationType === 'lines' && 'margin: 12px 0;'}
      ${_sliderPaginationType === 'bullets' && 'padding: 8px 0;'}

      ${_sliderPaginationType === 'thumbnails' &&
      `margin: ${_sliderPaginationType === 'numbers' ? '10px 0' : '20px 0'};`}

      ${_sliderPaginationType === 'thumbnails' &&
      'padding-left: 0; padding-right: 0;'}
    }

    & .${meta.prefix}-line-line .${meta.prefix}-progress-line {
      height: 2px;
    }

    & .${meta.prefix}-slider-pagination-item-active .${meta.prefix}-line-line {
      ${_progressLineActiveBg && `background-color: ${_progressLineActiveBg};`}
    }
  `}
`;

const Inner = styled.div.attrs({
  className: classNames(
    `${meta.prefix}-slider-inner`,
    `${meta.prefix}-slider-innerSlider`
  )
})`
  margin: 0 auto;
  align-items: stretch;
  justify-content: center;

  & .${meta.prefix}-slider-item {
    justify-content: center;
  }

  & .${meta.prefix}-slider-outer {
    width: ${({ _outerWidth }) => _outerWidth}px;
    margin: 0 auto;
    max-width: 100%;
  }

  & .${meta.prefix}-navigation-arrow {
    background-color: transparent;
  }

  & .${meta.prefix}-navigation-next {
    right: 0;
  }

  & .${meta.prefix}-navigation-previous {
    left: 0;
  }

  & .${meta.prefix}-navigation-next:hover {
    right: -4px;
  }

  & .${meta.prefix}-navigation-previous:hover {
    left: -4px;
  }
`;
